import { CategoryButton } from 'spa/components/button/category_button'
import { connect } from 'react-redux'
import { defineMessages, injectIntl } from 'react-intl'
import { standardFiltersShape } from 'spa/api/songs'
import intersection from 'lodash/intersection'
import PropTypes from 'prop-types'
import React from 'react'
import styles from './styles'

export const GENRE_LABELS = defineMessages({
  hiphop: {
    id: 'djcity.common.genre_label.hiphop',
    defaultMessage: 'Hip Hop',
  },
  dance: {
    id: 'djcity.common.genre_label.dance',
    defaultMessage: 'Dance',
  },
  latin: {
    id: 'djcity.common.genre_label.latin_genre',
    defaultMessage: 'Latin',
  },
  pop: {
    id: 'djcity.common.genre_label.pop',
    defaultMessage: 'Pop',
  },
  rnb: {
    id: 'djcity.common.genre_label.rnb',
    defaultMessage: 'R&B',
  },
  african: {
    id: 'djcity.common.genre_label.african',
    defaultMessage: 'African',
  },
  other: {
    id: 'djcity.common.genre_label.other',
    defaultMessage: 'Other',
  },
  2: {
    id: 'djcity.common.genre_label.asia_region',
    defaultMessage: 'Asia',
  },
  3: {
    id: 'djcity.common.genre_label.uk_region',
    defaultMessage: 'UK',
  },
  4: {
    id: 'djcity.common.genre_label.germany_region',
    defaultMessage: 'Germany',
  },
  6: {
    id: 'djcity.common.genre_label.latin_region',
    defaultMessage: 'Latin',
  },
  7: {
    id: 'djcity.common.genre_label.south_asia_region',
    defaultMessage: 'South Asia',
  },
})

export const BASE_GENRE_TAGS_MAP = Object.freeze({
  HIPHOP: 1,
  1: 'HIPHOP',
  DANCE: 102,
  102: 'DANCE',
  LATIN: 3,
  3: 'LATIN',
  POP: 4,
  4: 'POP',
  RNB: 5,
  5: 'RNB',
  AFRICAN: 36,
  36: 'AFRICAN',
  OTHER: 8,
  8: 'OTHER',
})

export const GENRE_TAGS = Object.freeze({
  HIPHOP: [1],
  DANCE: [102],
  LATIN: [3],
  POP: [4],
  RNB: [5],
  AFRICAN: [36],
  OTHER: [8],
  ASIA: [107, 130, 141, 190],
  GERMANY: [126, 127, 128, 131, 178, 197],
  UK: [22, 23, 24, 25, 26, 27, 28, 37, 54, 57, 78, 188],
  SOUTH_ASIA: [213, 244, 245, 246, 272, 283, 290],
})

export const REGION_IDS = {
  USA: 1,
  ASIA: 2,
  UK: 3,
  GERMANY: 4,
  REGGAE_NY: 5, // not used
  LATIN: 6,
  SOUTH_ASIA: 7,
  1: 'USA',
  2: 'ASIA',
  3: 'UK',
  4: 'GERMANY',
  5: 'REGGAE_NY', // not used
  6: 'LATIN',
  7: 'SOUTH_ASIA',
}

export const GENRE_TAG_TO_LABEL = {
  1: GENRE_LABELS.hiphop,
  102: GENRE_LABELS.dance,
  3: GENRE_LABELS.latin,
  4: GENRE_LABELS.pop,
  5: GENRE_LABELS.rnb,
  36: GENRE_LABELS.african,
  8: GENRE_LABELS.other,
  22: GENRE_LABELS[3],
  23: GENRE_LABELS[3],
  24: GENRE_LABELS[3],
  25: GENRE_LABELS[3],
  26: GENRE_LABELS[3],
  27: GENRE_LABELS[3],
  28: GENRE_LABELS[3],
  37: GENRE_LABELS[3],
  54: GENRE_LABELS[3],
  57: GENRE_LABELS[3],
  78: GENRE_LABELS[3],
  188: GENRE_LABELS[3],
  107: GENRE_LABELS[2],
  130: GENRE_LABELS[2],
  141: GENRE_LABELS[2],
  190: GENRE_LABELS[2],
  126: GENRE_LABELS[4],
  127: GENRE_LABELS[4],
  128: GENRE_LABELS[4],
  131: GENRE_LABELS[4],
  178: GENRE_LABELS[4],
  197: GENRE_LABELS[4],
  213: GENRE_LABELS[7],
  244: GENRE_LABELS[7],
  245: GENRE_LABELS[7],
  246: GENRE_LABELS[7],
  272: GENRE_LABELS[7],
  283: GENRE_LABELS[7],
  290: GENRE_LABELS[7],
}

class CategoryFilterPanel extends React.PureComponent {
  static propTypes = {
    children: PropTypes.node,
    filters: PropTypes.shape(standardFiltersShape),
    genrePreferences: PropTypes.object,
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    locale: PropTypes.string,
    onChange: PropTypes.func,
    preferences: PropTypes.arrayOf(
      PropTypes.shape({
        regionId: PropTypes.number.isRequired,
        selected: PropTypes.bool.isRequired,
        autoselected: PropTypes.bool.isRequired,
      })
    ),
  }

  componentDidUpdate(prevProps) {
    const { locale } = this.props

    if (prevProps.locale !== locale) {
      this.handleGenreChange([], 0)
    }
  }

  handleGenreChange = (genres, regionId = 0) => {
    const { filters, onChange } = this.props
    if (onChange) {
      onChange({ ...filters, genres, regionId, page: 1 })
    }
  }

  handleChange = (genre, regionId, checked) => {
    const { onChange } = this.props
    const newGenre = checked ? genre : null
    const newRegionId = checked ? regionId : 0
    if (onChange) {
      this.handleGenreChange(newGenre, newRegionId)
    }
  }

  render() {
    const {
      children,
      filters: { genres } = null,
      preferences = [],
      genrePreferences = {},
      intl,
    } = this.props

    const GenreButton = genreTagKey => (
      <CategoryButton
        checked={intersection(genres, GENRE_TAGS[genreTagKey]).length > 0}
        id={`genre-tag-${genreTagKey.toLowerCase()}`}
        key={genreTagKey}
        labelText={intl.formatMessage(
          GENRE_TAG_TO_LABEL[GENRE_TAGS[genreTagKey][0]]
        )}
        onChange={e =>
          this.handleChange(
            GENRE_TAGS[genreTagKey],
            REGION_IDS[genreTagKey] || 0,
            e.target.checked
          )
        }
      />
    )

    const FILTERED_GENRE_TAGS = Object.keys(GENRE_TAGS).filter(genreTagKey => {
      const hide_genre = `hide_${genreTagKey.toLowerCase()}`
      if (Object.keys(genrePreferences).includes(hide_genre)) {
        // if genre
        return !genrePreferences[hide_genre]
      } else {
        // else region
        const regionpreference = preferences.find(
          pref => pref.regionId === REGION_IDS[genreTagKey]
        )
        return (
          !regionpreference ||
          regionpreference.selected ||
          regionpreference.autoselected
        )
      }
    })

    const buttons = FILTERED_GENRE_TAGS.map(GenreButton)

    return (
      <div className={styles.checkboxRow}>
        {buttons}
        {children}
      </div>
    )
  }
}

const mapStateToProps = (state, props) => ({
  locale: state.locale ? state.locale.locale : null,
  preferences: state.preferences.preferences,
  genrePreferences: state.preferences.genrePreferences,
  ...props,
})

const ConnectedCategoryFilterPanel = connect(mapStateToProps)(
  injectIntl(CategoryFilterPanel)
)

export { ConnectedCategoryFilterPanel as CategoryFilterPanel }
